import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  IntentBadge,
  StatusBadge,
  formatDateTime,
} from "@excellent/ui";
import { apiCall, apiKey, useApi } from "@excellent/ui/api";
import { useSWRConfig } from "swr";
import {
  integrationConflicts,
  type IntegrationConflict,
} from "@excellent/ui/api/contracts/integration-conflicts";

/**
 * Integration conflicts resolution inbox (Data Design §4.6 / D5). Self-fetches the
 * open queue via the mounted list contract so Vite pages need no server props.
 */
export function ConflictsQueue({ initial }: { initial?: IntegrationConflict[] } = {}) {
  const { mutate } = useSWRConfig();
  const { data } = useApi(integrationConflicts.list, { query: { status: "open" } });
  const [rows, setRows] = useState<IntegrationConflict[]>(initial ?? []);
  const [busy, setBusy] = useState<string | null>(null);

  // Prefer live list once it arrives.
  const live = data?.conflicts;

  async function resolve(
    uid: string,
    resolution: "ours" | "theirs" | "merged",
    mergedValue?: string,
  ) {
    setBusy(uid);
    try {
      const res = await apiCall(integrationConflicts.resolve, {
        body: { uid, resolution, merged_value: mergedValue },
      });
      if (res.ok) {
        // Optimistically drop the resolved row from the open queue.
        setRows((prev) => prev.filter((r) => r.uid !== uid));
        void mutate(apiKey(integrationConflicts.list, { query: { status: "open" } }));
      }
    } finally {
      setBusy(null);
    }
  }

  // When live data arrives, prefer it; optimistic setRows still used after resolve.
  useEffect(() => {
    if (live) setRows(live);
  }, [live]);

  const open = (live ?? rows).filter((r) => r.status === "open");

  if (open.length === 0) {
    return (
      <Card className="p-6 type-body text-muted-foreground">
        No open conflicts. Synced values are landing cleanly — a high-stakes field that
        diverges from a locally-edited value will surface here before it overwrites.
      </Card>
    );
  }

  return (
    <Card className="divide-y divide-border/40">
      {open.map((c) => (
        <ConflictRow key={c.uid} conflict={c} busy={busy === c.uid} onResolve={resolve} />
      ))}
    </Card>
  );
}

function ConflictRow({
  conflict,
  busy,
  onResolve,
}: {
  conflict: IntegrationConflict;
  busy: boolean;
  onResolve: (uid: string, resolution: "ours" | "theirs" | "merged", mergedValue?: string) => void;
}) {
  const [merging, setMerging] = useState(false);
  const [mergeVal, setMergeVal] = useState(conflict.theirs_value ?? "");

  return (
    <div className="flex flex-col gap-3 p-4 first:pt-4 last:pb-4">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="truncate font-medium">{conflict.field}</span>
            <IntentBadge intent="warning" className="font-normal">
              High-stakes
            </IntentBadge>
          </div>
          <p className="truncate type-subtext text-muted-foreground">
            {conflict.urn}
            {conflict.theirs_source ? ` · from ${conflict.theirs_source}` : ""} ·{" "}
            {formatDateTime(conflict.created_at)}
          </p>
        </div>
        <StatusBadge domain="task" status="review" label="Open" />
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <ValuePane label="Ours (local)" value={conflict.ours_value} />
        <ValuePane label="Theirs (synced)" value={conflict.theirs_value} />
      </div>

      {merging ? (
        <div className="flex items-center gap-2">
          <input
            className="h-7 flex-1 rounded-sm border border-border bg-background px-2 type-body"
            value={mergeVal}
            onChange={(e) => setMergeVal(e.target.value)}
            placeholder="Merged value"
          />
          <Button
            variant="accept"
            disabled={busy}
            onClick={() => onResolve(conflict.uid, "merged", mergeVal)}
          >
            Save merge
          </Button>
          <Button variant="ghost" onClick={() => setMerging(false)}>
            Cancel
          </Button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="accept"
            disabled={busy}
            onClick={() => onResolve(conflict.uid, "theirs")}
          >
            Accept theirs
          </Button>
          <Button
            variant="reject"
            disabled={busy}
            onClick={() => onResolve(conflict.uid, "ours")}
          >
            Keep ours
          </Button>
          <Button variant="ghost" disabled={busy} onClick={() => setMerging(true)}>
            Merge…
          </Button>
        </div>
      )}
    </div>
  );
}

function ValuePane({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="rounded-xs border border-border/40 p-2">
      <p className="type-subtext font-medium text-muted-foreground">{label}</p>
      <p className="mt-0.5 break-words type-body">{value ?? <span className="text-muted-foreground">—</span>}</p>
    </div>
  );
}
